/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { useRecoilState } from "recoil";
import { ActiveEmailAtom } from "../../../atoms/ActiveEmailAtom";
import RRuleGeneratorTS, {
  translations,
} from "../../../components/RRuleGenerator";
import useMediaQuery from "../../../hook/useMediaQuery";
import DateTimePicker from "../../DateTimePicker";
import StyledWritableSelect from "../../Settings/StyledWritableSelect";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import StyledSwitch from "../../Switch";
import { useModal } from "../Modal";

const ModalWrapper = styled.div`
  width: 700px;
  height: 70vh;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  padding: 24px;
  margin: 20px -22px -25px;

  border-top: 1px solid #e8e8e8;

  overflow-y: scroll;
  overflow-x: hidden;

  z-index: 0;

  strong {
    font-weight: 600;
  }

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: 100%;
    min-height: 100vh;
    margin: 0;

    border-top: none;
  }
`;

const ModalContent = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;

  @media screen and (max-width: 600px) {
    height: 100%;
  }
`;

const ModalFooter = styled.div`
  width: calc(100% + 48px);

  border-top: 1px solid #e8e8e8;

  padding: 24px 24px 0 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 600px) {
    width: 100%;

    padding: 24px;

    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    justify-content: space-between;
    grid-gap: 16px;
    flex-wrap: wrap;

    button {
      width: calc(50% - 16px);
    }
  }
`;

const FormWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  grid-gap: 14px;
  margin-bottom: 25px;

  position: relative;

  > div {
    width: 100% !important;
  }

  .px-0.pt-3.border.rounded {
    width: 100% !important;
    padding-top: 0 !important;
  }

  #rrule-5-repeat-frequency,
  #rrule-4-repeat-frequency,
  #rrule-2-repeat-frequency,
  #rrule-1-repeat-frequency {
    display: none;
  }

  /* .form-group.m-0.row.d-flex.align-items-center {
    display: flex !important;
    align-items: center !important;
    justify-content: start !important;

    grid-gap: 16px !important;
  } */

  .px-0.pt-3.border.rounded {
    margin: 0 -16px;
  }

  .form-group.row.d-flex.align-items-sm-center {
    /* display: flex !important; */
    align-items: center !important;
    justify-content: start !important;

    grid-gap: 16px !important;

    .col-sm-1,
    .col-sm-2,
    .col-sm-3 {
      width: auto !important;
    }
  }

  .px-0.pt-3.border.rounded > div:first-of-type > .px-3 > .form-group.row {
    display: none;
  }

  input#rrule-1-repeat-monthly-interval,
  input#rrule-1-repeat-weekly-interval,
  input#rrule-1-repeat-daily-interval {
    width: 64px !important;
  }

  .btn-primary {
    color: #222 !important;
    background-color: #fff2e5 !important;
    border-color: #fff2e5 !important;
  }

  .btn-primary:not(:disabled):not(.disabled).active,
  .btn-primary:not(:disabled):not(.disabled):active,
  .show > .btn-primary.dropdown-toggle {
    color: #fff !important;
    background-color: #fb972e !important;
    border-color: #fb972e !important;
  }

  .btn-group.btn-group-toggle.offset-sm-2 {
    display: grid;
    grid-template-columns: repeat(7, 32px);
    grid-gap: 8px;

    > label {
      width: 32px;
      height: 32px;

      border-radius: 50%;

      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .form-group.row {
    grid-gap: 16px !important;
  }
`;

const FlexButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > button + button {
    margin-left: 10px;
  }
`;

const FormLabeLInput = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  > div {
    width: 100%;
  }
`;

const LabelTitle = styled.div`
  font-size: 16px;
  font-weight: 600;

  margin-bottom: 16px;
`;

const textAreaStyles = {
  border: "1px solid #dddddd",
  borderRadius: "4px",
  padding: "8px",
};

const InputWrapper = styled.div`
  width: 100%;
  height: 40px;

  .css-193c1p1-Container-notSelect {
    margin: 0;
  }

  .css-1e98oes-InputContainer {
    border: 1px solid rgb(221, 221, 221);
  }

  .css-1e98oes-InputContainer,
  #writableSelect {
    height: 40px;
  }
`;

const RepeatOption = styled.div<{ selected: boolean }>`
  border: 1px solid #fb972e;
  width: 100%;
  text-align: center;
  padding: 9px 62px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  color: ${(props) => (props.selected ? "#fff" : "#333333")};

  background-color: ${(props) => (props.selected ? "#fb972e" : "#fff")};
`;

const RepeatOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  margin-bottom: 25px;
`;

const RepeatOptionsContainer = styled.div<{ length: number }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.length}, 1fr)`};

  > div:first-of-type {
    border-radius: 24px 0px 0px 24px;
  }

  > div:not(:first-of-type):not(:last-of-type) {
    border-style: solid none;
  }

  > div:last-of-type {
    border-radius: 0px 24px 24px 0px;
  }
`;

const ExpandableDiv = styled.div<{ isCalendarOpen: boolean }>`
  margin-left: ${(props) => (props.isCalendarOpen ? "330px" : "0px")};
  width: ${(props) =>
    props.isCalendarOpen ? "calc(48% + 6px) !important" : "100%"};

  > div > textarea {
    width: ${(props) => (props.isCalendarOpen ? "calc(48% + 6px)" : "100%")};
  }

  @media screen and (max-width: 821px) {
    margin-left: ${(props) => (props.isCalendarOpen ? "392px" : "0px")};
    width: ${(props) =>
      props.isCalendarOpen ? "calc(48% + 6px) !important" : "100%"};

    > div > textarea {
      width: ${(props) => (props.isCalendarOpen ? "calc(48% + 6px)" : "100%")};
    }
  }

  @media screen and (max-width: 769px) {
    margin-left: ${(props) => (props.isCalendarOpen ? "366px" : "0px")};
    width: ${(props) =>
      props.isCalendarOpen ? "calc(48% + 6px) !important" : "100%"};

    > div > textarea {
      width: ${(props) => (props.isCalendarOpen ? "calc(48% + 6px)" : "100%")};
    }
  }

  @media screen and (max-width: 600px) {
    margin-left: 0;
    width: 100% !important;

    > div > textarea {
      width: 100% !important;
    }
  }
`;

const repeatOptions: Option[] = [
  {
    id: "1",
    name: "Daily",
    value: "Daily",
  },
  {
    id: "2",
    name: "Weekly",
    value: "Weekly",
  },
  {
    id: "3",
    name: "Monthly",
    value: "Monthly",
  },
];

interface Option {
  id: string;
  name: string;
  value: string;
}

const durationOptions: Option[] = [
  { id: "1", name: "15 min", value: "15" },
  { id: "2", name: "30 min", value: "30" },
  { id: "3", name: "45 min", value: "45" },
  { id: "4", name: "1 hour", value: "60" },
  { id: "5", name: "1.5 hour", value: "90" },
  { id: "6", name: "2 hours", value: "120" },
];

interface CalendarLinksProps {
  editor: Editor;
}

export default function CalendarLinks({ editor }: CalendarLinksProps) {
  const [isRecurring, setIsRecurring] = useState(false);
  const [date, setDate] = useState<string | undefined>();
  const [time, setTime] = useState<string | undefined>();
  const [scheduleDate, setScheduleDate] = useState<Date>();
  const [calendarKey, setCalendarKey] = useState<string>("");
  const modal = useModal();
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState<number | null>();
  const [clearDurations, setClearDurations] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(true);
  const isLargerThan600 = useMediaQuery("(min-width: 600px)");

  const [recurrenceFrequency, setRecurrenceFrequency] = useState("");

  // Set the starting recurrence to be the start of the recurring event
  const [recurrence, setRecurrence] = useState("RRULE:FREQ=DAILY;INTERVAL=1");

  const [activeEmail, setActiveEmail] = useRecoilState(ActiveEmailAtom);

  // Query selector to pick RRuleGeneratorTS' recurrency select
  let selectFromRRuleGenerator = useRef(null);
  let event = new Event("change", { bubbles: true });

  let endRecurrencyFromRRuleGenerator = useRef(null);

  useEffect(() => {
    // Wait render until select is visible
    if (isRecurring) {
      setRecurrenceFrequency("1");
      setRecurrence("RRULE:FREQ=DAILY;INTERVAL=1");

      selectFromRRuleGenerator.current = document.querySelector(
        "[id*=repeat-frequency]"
      );
      //Start repeat frequency with "Daily" option selected
      selectFromRRuleGenerator.current.value = "Daily";
      selectFromRRuleGenerator.current.dispatchEvent(event);
    }
  }, [isRecurring]);

  useEffect(() => {
    // Wait render until select is visible
    if (isRecurring) {
      endRecurrencyFromRRuleGenerator.current =
        window.document.getElementsByName("end.mode")[0] as HTMLSelectElement;
      //Start end frequency with "After"(second option) selected
      //as Vitor asked
      endRecurrencyFromRRuleGenerator.current.selectedIndex = 1;
      endRecurrencyFromRRuleGenerator.current.dispatchEvent(event);
    }
  }, [isRecurring]);

  function resetToInitialValues() {
    setRecurrenceFrequency("1");
    setRecurrence("RRULE:FREQ=DAILY;INTERVAL=1");
    setIsRecurring(false);
    setDescription("");
    setSummary("");
    setCalendarKey("");
    setScheduleDate(undefined);
    setDuration(null);
    setClearDurations(true);
  }

  function AddScheduleToEmail(type: "button" | "raw" | "link") {
    let append = "";

    // Creates an event that will be saved with the email
    const event = {
      summary,
      description,
      recurrence: isRecurring ? recurrence : undefined,
      start: scheduleDate,
      duration,
    };

    switch (type) {
      case "button":
        append = `
Add to your calendar with the links below:
{{calendar_buttons}}
`;
        break;
      case "raw":
        const a = `Add to your calendar with the links below:
        Add to Google
{{calendar_google_raw}}
`;
        append = `Add to Outlook
{{calendar_outlook_raw}}
Add to Yahoo
{{calendar_yahoo_raw}}
`;
        break;
      case "link":
        append = `
Add to your calendar with the links below:
{{calendar_links}}
`;
        break;
    }

    editor.chain().focus().insertContent(append).run();

    modal.popModal();
  }

  function sortHTMLSelectElement(HTMLOptions: HTMLOptionsCollection) {
    let auxArray: string[][] = [];

    //selectedElement param has .options attributes w/ text and value inside
    //HTMLOptionCollection: interface to a collection of HTML <options>

    for (let i = 0; i < HTMLOptions.length; i++) {
      //Building the option list
      auxArray[i] = [];
      auxArray[i][0] = HTMLOptions[i].text;
      auxArray[i][1] = HTMLOptions[i].value;
    }

    //auxArray receive text and value per iteration and sorts it
    auxArray.sort();

    //remove all options from the select
    while (HTMLOptions.length > 0) {
      //@ts-ignore
      HTMLOptions[0] = null;
    }
    for (let i = 0; i < auxArray.length; i++) {
      //new Option() creates a new HTMLOptionElement with the new order
      let op = new Option(auxArray[i][0], auxArray[i][1]);
      HTMLOptions[i] = op;
    }
    return;
  }

  useEffect(() => {
    // Remove hourly because google doesn't accept this...
    if (isRecurring) {
      const element = window.document.getElementsByName(
        "repeat.frequency"
      )[0] as HTMLSelectElement;
      element?.options.remove(0);
      element?.options.remove(3);
      sortHTMLSelectElement(element.options);
    }
  }, [isRecurring]);

  useEffect(() => {
    // Change text in RRuleGeneratorTS component (from 'executions' to 'ocurrences')
    let xpath = "//div[contains(text(),'executions.')]";
    const elementText = document.evaluate(
      xpath,
      document,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue as HTMLDivElement | null;
    if (elementText) {
      elementText.innerText = "ocurrences.";
    }
  }, [recurrence]);

  useEffect(() => {
    // Hiding recurrency select from RRuleGeneratorTS
    const elementText = document.querySelector(
      "select[name='repeat.frequency']"
    );

    if (elementText) {
      elementText.setAttribute("style", "visibility: hidden");
    }
  }, [isRecurring]);

  useEffect(() => {
    const weekDay = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    weekDay.map((day) => {
      const element = document.querySelector(
        `input[name="repeat.weekly.days[${day}]"]`
      );
      if (element) element.nextSibling.textContent = day[0].toUpperCase();
    });
  }, [recurrence]);

  function getDateAndTime(fromDate: Date) {
    const offset = fromDate.getTimezoneOffset() * 60 * 1000;
    const localTime = new Date(fromDate.getTime() - offset);
    const [day, timeAndPeriod] = localTime.toISOString().split("T");
    const [hour] = timeAndPeriod.split("Z");

    return [day, hour];
  }

  function handleDateSelect(selectedDate: Date) {
    const [day, hour] = getDateAndTime(selectedDate);
    setDate(day);

    if (time && selectedDate.getMinutes() !== 21) {
      const mountedDateTime = new Date(`${day} ${time}`);

      setScheduleDate(mountedDateTime);
    } else {
      let newMinutes = parseInt(hour.split(":")[1], 10);
      let newSelectedDate = new Date(selectedDate);

      const atualMinutes = newMinutes;
      if (newMinutes % 5 === 0 || newMinutes === 21) {
        setTime(hour);
        setScheduleDate(selectedDate);
      } else {
        while (newMinutes % 5 !== 0) {
          newMinutes += 1;
        }

        newSelectedDate = dayjs(newSelectedDate)
          .add(newMinutes - atualMinutes, "minutes")
          .toDate();

        const [, newHour] = getDateAndTime(newSelectedDate);

        setTime(newHour);
        setScheduleDate(newSelectedDate);
      }
    }
  }

  function handleTimeSelect(selectedDate: Date) {
    const [day, hour] = getDateAndTime(selectedDate);
    setTime(hour);

    if (date) {
      const mountedDateTime = new Date(`${date} ${hour}`);

      setScheduleDate(mountedDateTime);
    }
  }

  return (
    <ModalWrapper>
      <ModalContent>
        <FormWrapper
          css={css`
            @media screen and (max-width: 600px) {
              flex-direction: column;
            }
          `}
        >
          <FormLabeLInput
            css={css`
              min-width: 300px;
            `}
          >
            <LabelTitle>Date</LabelTitle>

            <InputWrapper
              css={css`
                width: 300px !important;

                @media screen and (max-width: 1024px) {
                  width: 100% !important;
                }
              `}
            >
              <DateTimePicker
                date={scheduleDate}
                onDateSelect={handleDateSelect}
                onTimeSelect={handleTimeSelect}
                calendarKey={calendarKey}
                setCalendarKey={setCalendarKey}
                showDatePicker
                showTimePicker
                input
                isCalendarOpen={(open) => setIsCalendarOpen(open)}
              />
            </InputWrapper>
          </FormLabeLInput>

          <FormLabeLInput>
            <LabelTitle>Duration</LabelTitle>

            <InputWrapper>
              <StyledWritableSelect
                options={durationOptions}
                selectedOption={`${duration}`}
                handleSelectedOption={(e) => {
                  setDuration(+e);
                }}
                clearAll={clearDurations}
                isDuration
              />
            </InputWrapper>
          </FormLabeLInput>
        </FormWrapper>

        <FormWrapper>
          <FormLabeLInput>
            <ExpandableDiv isCalendarOpen={isCalendarOpen}>
              <LabelTitle>Event Name</LabelTitle>

              <InputWrapper>
                <StyledInput
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Enter event name"
                />
              </InputWrapper>
            </ExpandableDiv>
          </FormLabeLInput>
        </FormWrapper>

        <FormWrapper>
          <FormLabeLInput>
            <ExpandableDiv isCalendarOpen={isCalendarOpen}>
              <LabelTitle>Description</LabelTitle>
              <TextareaAutosize
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                minRows={4}
                placeholder="Enter event description"
                className={"border-hover"}
                style={{
                  width: "100%",
                  outline: "none",
                  fontSize: "14px",
                  padding: "5px",
                  color: "#2f2f2f",
                  resize: "none",
                  border: "none",
                  ...textAreaStyles,
                }}
                maxRows={99999}
              />
            </ExpandableDiv>
          </FormLabeLInput>
        </FormWrapper>

        <FormWrapper
          css={css`
            justify-content: start;

            border-bottom: 1px solid #e8e8e8;
            padding-bottom: 24px;

            @media screen and (max-width: 1024px) {
              border-bottom: none;
              padding-bottom: 0;
            }

            @media screen and (max-width: 560px) {
              display: none;
            }

            > div {
              width: unset !important;
            }
          `}
        >
          <LabelTitle
            css={css`
              margin-bottom: 0;
            `}
          >
            Recurring Event
          </LabelTitle>

          <StyledSwitch
            toggled={isRecurring}
            whenToggled={() => setIsRecurring(!isRecurring)}
            noInfo
          />
        </FormWrapper>

        {isRecurring && (
          <RepeatOptionsWrapper>
            <LabelTitle>Repeat</LabelTitle>
            <RepeatOptionsContainer length={repeatOptions.length}>
              {repeatOptions.map((opt) => {
                return (
                  <RepeatOption
                    selected={recurrenceFrequency === opt.id}
                    onClick={() => {
                      setRecurrenceFrequency(opt.id);
                      if (!selectFromRRuleGenerator) return;
                      //@ts-ignore
                      if (selectFromRRuleGenerator.current) {
                        selectFromRRuleGenerator.current.value = opt.value;
                        selectFromRRuleGenerator.current?.dispatchEvent(event);
                      }
                    }}
                  >
                    {opt.name}
                  </RepeatOption>
                );
              })}
            </RepeatOptionsContainer>
          </RepeatOptionsWrapper>
        )}

        {isRecurring && (
          <FormWrapper>
            <RRuleGeneratorTS
              onChange={(rrule: any) => setRecurrence(rrule)}
              value={""}
              translations={translations.english}
              config={{
                weekStartsOnSunday: true,
              }}
            />
          </FormWrapper>
        )}
      </ModalContent>

      <ModalFooter>
        {isLargerThan600 ? (
          <div>
            <StyledButton
              onClick={() => {
                resetToInitialValues();
              }}
              style={{
                marginRight: "20px",
                width: "100px",
              }}
              inverted
            >
              Reset
            </StyledButton>
          </div>
        ) : (
          <StyledButton
            onClick={() => {
              resetToInitialValues();
            }}
            inverted
          >
            Reset
          </StyledButton>
        )}

        {isLargerThan600 ? (
          <FlexButtonsWrapper>
            <StyledButton
              disable={!(duration && description && summary)}
              onClick={() => AddScheduleToEmail("link")}
            >
              Add Text Links
            </StyledButton>
            <StyledButton
              disable={!(duration && description && summary)}
              onClick={() => AddScheduleToEmail("raw")}
            >
              Add HTML Links
            </StyledButton>
            <StyledButton
              disable={!(duration && description && summary)}
              onClick={() => AddScheduleToEmail("button")}
            >
              Add Buttons
            </StyledButton>
          </FlexButtonsWrapper>
        ) : (
          <>
            <StyledButton
              disable={!(duration && description && summary)}
              onClick={() => AddScheduleToEmail("link")}
            >
              Add Text Links
            </StyledButton>
            <StyledButton
              disable={!(duration && description && summary)}
              onClick={() => AddScheduleToEmail("raw")}
            >
              Add HTML Links
            </StyledButton>
            <StyledButton
              disable={!(duration && description && summary)}
              onClick={() => AddScheduleToEmail("button")}
            >
              Add Buttons
            </StyledButton>
          </>
        )}
      </ModalFooter>
    </ModalWrapper>
  );
}
